import React, { useRef } from 'react';
import {
  Badge, Box, Button, Checkbox, ClickAwayListener, FormControl,
  FormControlLabel, FormGroup, Grow, Paper, Popper, Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { dataConstants } from '../../../constants';

const { SECTOR_OPTIONS } = dataConstants;
const sectorNames = SECTOR_OPTIONS.map(s => s.shortName);

export default function SectorFilter({ selectedSectors, onChange, open, onToggle, onClose }) {
  const anchorRef = useRef(null);

  const allChecked = selectedSectors.length === sectorNames.length;
  const indeterminate = selectedSectors.length > 0 && !allChecked;

  const handleClose = (e) => {
    if (anchorRef.current?.contains(e.target)) return;
    onClose();
  };

  const handleCheckAll = (e) => {
    onChange(e.target.checked ? [...sectorNames] : []);
  };

  const handleCheck = (name) => (e) => {
    if (e.target.checked) {
      onChange([...selectedSectors, name]);
    } else {
      onChange(selectedSectors.filter(s => s !== name));
    }
  };

  const count = selectedSectors.length;

  // Build inline label showing selected sectors
  const buttonLabel = count === 0
    ? 'Sector'
    : count <= 2
      ? selectedSectors.join(', ')
      : `${selectedSectors.slice(0, 2).join(', ')} +${count - 2}`;

  return (
    <>
      <Badge badgeContent={count} color="primary" invisible={!count}
        sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
      >
        <Button
          ref={anchorRef}
          size="small"
          variant={count ? 'contained' : 'outlined'}
          disableElevation
          onClick={onToggle}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{
            textTransform: 'none', fontSize: '0.82rem', borderRadius: '8px',
            px: 1.5, py: 0.5, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            ...(count ? {
              bgcolor: 'primary.main', color: '#fff',
              '&:hover': { bgcolor: 'primary.dark' },
            } : {
              borderColor: 'rgba(0,0,0,0.15)', color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }), minWidth: { sm: 'auto', md: 0 },
          }}
        >
          {buttonLabel}
        </Button>
      </Badge>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start"
        transition disablePortal style={{ zIndex: 20 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <Paper sx={{
              mt: 0.5, p: 2, maxWidth: 420, boxShadow: 4, borderRadius: '8px',
              '& .MuiTypography-body1': { fontSize: '.82rem' },
            }}>
              <ClickAwayListener onClickAway={handleClose}>
                <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                      label={<Typography sx={{ fontWeight: 600, fontSize: '.82rem' }}>Select All</Typography>}
                      control={
                        <Checkbox size="small" checked={allChecked} indeterminate={indeterminate}
                          onChange={handleCheckAll} sx={{ py: 0.25 }} />
                      }
                    />
                    {count > 0 && (
                      <Button size="small" onClick={() => onChange([])}
                        sx={{ textTransform: 'none', fontSize: '.78rem', minWidth: 0 }}>
                        Clear
                      </Button>
                    )}
                  </Box>
                  <FormGroup sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', ml: 1 }}>
                    {sectorNames.map(name => (
                      <FormControlLabel
                        key={name}
                        sx={{ width: { xs: '100%', sm: '48%' }, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '.82rem' } }}
                        control={
                          <Checkbox size="small" checked={selectedSectors.includes(name)}
                            onChange={handleCheck(name)} sx={{ py: 0.25 }} />
                        }
                        label={name}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
